import { css } from "@linaria/core";
import cx from "classnames";
import React, { memo, useMemo } from "react";
import t from "../../../../../../../../../../i18n";
import Badge from "@ui/Badge";
import Text from "@ui/Text";
import { colors, spacings } from "@ui/Theme";
import { useGUIEditorContext } from "../../../../../../../hook/context/gui";
import Selector from "@ui/Selector";
import { useLayerSwapEncodingCallback } from "../../hook/useLayerSwapEncodingCallback";
import { useLayerUpdateEncodingPositionCallback } from "../../hook/useLayerUpdateEncodingPositionCallback";
import { AxeX, LeftYAxis, RightYAxis } from "@ui/IconV2/react";

const LayerSettingHeaderContainer = css`
  align-items: center;
  display: grid;
  row-gap: 8px;
  grid-template-columns: 1fr max-content;
  grid-template-rows: 32px;
  transition-duration: var(--section-column-transition-duration);
  transition-timing-function: var(--section-column-transition-ease);
  transition-property: opacity, grid-template-rows, gap, margin-top;

  &:not(:first-of-type) {
    border-top: 1px solid ${colors.titanium200};
    padding-top: ${spacings.m};
  }

  & + & {
    margin-top: 8px;
  }
`;

const LayerSettingHeaderContainerHidden = css`
  opacity: 0;
  pointer-events: none;
  /* display: none; */
`;

const LayerSettingHeaderContainerFocused = css`
  grid-template-rows: 0px;
  gap: 0;
  margin-top: 0;
`;

const LayerAxis = css`
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity var(--section-column-transition-duration)
    var(--section-column-transition-ease);
`;

const LayerAxisSelector = css`
  svg :is(rect, path) {
    fill: ${colors.carbon800};
  }
`;

const HiddenLayerAxis = css`
  opacity: 0;
  pointer-events: none;
`;

export const LayerSettingHeader = memo(
  ({
    index,
    unit,
    axis,
    position,
  }: {
    index: number;
    unit: string | null;
    axis: string;
    position: number;
  }) => {
    const { block, preset, focusedEncoding } = useGUIEditorContext();
    const layerSwapEncoding = useLayerSwapEncodingCallback(position);
    const layerUpdateEncodingPosition = useLayerUpdateEncodingPositionCallback(position);

    const isFocused = useMemo(
      () => focusedEncoding?.layer === position && focusedEncoding?.axis === axis,
      [focusedEncoding?.layer, focusedEncoding?.axis, position, axis],
    );

    return (
      <header
        className={cx(LayerSettingHeaderContainer, {
          [LayerSettingHeaderContainerHidden]: focusedEncoding,
          [LayerSettingHeaderContainerFocused]: isFocused,
        })}
      >
        <Text
          as="div"
          className={cx(LayerAxis, {
            [HiddenLayerAxis]: false,
          })}
        >
          {t(
            "project.dashboard.editor.gui.section.encoding.title",
            preset === "line" ? index : undefined,
          )}
          {unit && preset === "line" && (
            <Badge backgroundColor={colors.carbon100} color={colors.carbon800}>
              {unit}
            </Badge>
          )}
        </Text>
        {preset === "scatter" && (
          <Selector
            options={[
              { label: <AxeX />, value: "x" },
              { label: <LeftYAxis />, value: "y" },
            ]}
            value={axis}
            onChange={layerSwapEncoding}
          />
        )}
        {preset === "line" && (
          <Selector
            options={[
              { label: <LeftYAxis />, value: "left" },
              { label: <RightYAxis />, value: "right" },
            ]}
            value={
              block?.config.layers[position]?.position ?? (position % 2 === 0 ? "left" : "right")
            }
            onChange={position => layerUpdateEncodingPosition(position)}
            className={LayerAxisSelector}
          />
        )}
      </header>
    );
  },
);
